import type { Edge, Node } from 'reactflow';

const COLUMN_GAP = 60;
const ROW_GAP = 70;
const DEFAULT_WIDTH = 220;
const DEFAULT_HEIGHT = 130;

/**
 * Organiza o fluxo em camadas, de cima para baixo, a partir do bloco Início.
 *
 * Cada bloco fica uma camada abaixo do mais baixo dos blocos que levam a ele
 * (maior caminho a partir de uma raiz), então toda conexão desce. Dentro da
 * camada, a ordem segue a dos pais e das saídas, o que mantém os ramos de uma
 * Condição lado a lado. Laços de volta são ignorados no cálculo.
 */
export function layoutFlow(nodes: Node[], edges: Edge[]): Node[] {
  if (nodes.length === 0) return nodes;

  const ids = new Set(nodes.map((n) => n.id));
  const children = new Map<string, string[]>();
  const incoming = new Map<string, number>();
  nodes.forEach((n) => { children.set(n.id, []); incoming.set(n.id, 0); });

  const handleOrder = (h?: string | null) => {
    if (!h) return 0;
    if (h === 'true') return 0;
    if (h === 'false' || h === 'default') return 1000;
    const m = /(\d+)$/.exec(h);
    return m ? Number(m[1]) : 500;
  };
  [...edges]
    .filter((e) => ids.has(e.source) && ids.has(e.target) && e.source !== e.target)
    .sort((a, b) => handleOrder(a.sourceHandle) - handleOrder(b.sourceHandle))
    .forEach((e) => {
      children.get(e.source)!.push(e.target);
      incoming.set(e.target, (incoming.get(e.target) || 0) + 1);
    });

  const isTrigger = (n: Node) => (n.data?.nodeType || n.type) === 'trigger';
  const roots = nodes
    .filter((n) => incoming.get(n.id) === 0)
    .sort((a, b) => Number(isTrigger(b)) - Number(isTrigger(a)) || a.position.x - b.position.x);
  if (roots.length === 0) roots.push(nodes.find(isTrigger) || nodes[0]);

  // Laços (arestas de volta) são descartados para o cálculo das camadas
  const back = new Set<string>();
  const state = new Map<string, 1 | 2>(); // 1 = na pilha, 2 = concluído
  const dfs = (id: string) => {
    state.set(id, 1);
    for (const child of children.get(id)!) {
      if (state.get(child) === 1) back.add(`${id}>${child}`);
      else if (!state.has(child)) dfs(child);
    }
    state.set(id, 2);
  };
  roots.forEach((r) => dfs(r.id));
  nodes.forEach((n) => { if (!state.has(n.id)) dfs(n.id); });
  const forward = (id: string) => children.get(id)!.filter((c) => !back.has(`${id}>${c}`));

  // Camada = maior caminho a partir de uma raiz: cada bloco fica abaixo de todos os anteriores
  const level = new Map<string, number>();
  const indeg = new Map<string, number>();
  nodes.forEach((n) => indeg.set(n.id, 0));
  nodes.forEach((n) => forward(n.id).forEach((c) => indeg.set(c, indeg.get(c)! + 1)));
  const queue = nodes.filter((n) => indeg.get(n.id) === 0).map((n) => n.id);
  queue.forEach((id) => level.set(id, 0));
  while (queue.length) {
    const id = queue.shift()!;
    for (const child of forward(id)) {
      level.set(child, Math.max(level.get(child) ?? 0, level.get(id)! + 1));
      indeg.set(child, indeg.get(child)! - 1);
      if (indeg.get(child) === 0) queue.push(child);
    }
  }

  // Ordem dentro da camada: descoberta a partir das raízes, seguindo as saídas em ordem
  const order = new Map<string, number>();
  let seq = 0;
  const visit = [...roots.map((r) => r.id)];
  const seen = new Set(visit);
  while (visit.length) {
    const id = visit.shift()!;
    order.set(id, seq++);
    for (const child of children.get(id)!) {
      if (!seen.has(child)) { seen.add(child); visit.push(child); }
    }
  }
  nodes.forEach((n) => { if (!order.has(n.id)) order.set(n.id, seq++); });

  const layers: Node[][] = [];
  nodes.forEach((n) => { (layers[level.get(n.id)!] ||= []).push(n); });

  const width = (n: Node) => n.width || DEFAULT_WIDTH;
  const height = (n: Node) => n.height || DEFAULT_HEIGHT;
  const positions = new Map<string, { x: number; y: number }>();
  let y = 0;
  for (const layer of layers) {
    if (!layer) continue;
    layer.sort((a, b) => order.get(a.id)! - order.get(b.id)!);
    const total = layer.reduce((sum, n) => sum + width(n), 0) + COLUMN_GAP * (layer.length - 1);
    let x = -total / 2;
    for (const n of layer) {
      positions.set(n.id, { x, y });
      x += width(n) + COLUMN_GAP;
    }
    y += Math.max(...layer.map(height)) + ROW_GAP;
  }

  return nodes.map((n) => ({ ...n, position: positions.get(n.id) || n.position }));
}
